// src/pages/Sanitary/SanitaryReportsPage.jsx
// =============================================================================
// Sanitary / Hygiene and disinfection reports
// =============================================================================

import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";
import AuthContext from "../../context/AuthContext";
import {
  downloadSanitaryReportExcel,
  downloadSanitaryReportPdf,
  fetchSanitaryReports,
  generateSanitaryReport,
  lockSanitaryReport,
  regenerateSanitaryReport,
} from "../../services/sanitaryReportService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";
import "./SanitaryReportsPage.css";

function normalizeRole(role) {
  return String(role || "").trim().toLowerCase();
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function countBySeverity(findings = [], severity) {
  return asArray(findings).filter((f) => String(f.severity) === severity).length;
}

function summaryValue(report, key, fallback = 0) {
  return report?.summary_json?.[key] ?? fallback;
}

const PDF_DETAIL_LEVELS = Object.freeze({
  SUMMARY: "summary",
  DETAILED: "detailed",
  AUDIT: "audit",
});

const PDF_ORIENTATIONS = Object.freeze({
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape",
});

export default function SanitaryReportsPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const { authState } = useContext(AuthContext);
  const role = normalizeRole(authState?.user?.role);
  const canWrite = role === "superuser" || role === "reception";

  const period = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.THREE_MONTHS,
      EVALUATION_PERIOD_TYPES.SIX_MONTHS,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ],
  });
  const [filters, setFilters] = useState({ status: "" });
  const selectedPeriod = useMemo(() => ({ period_from: period.from, period_to: period.to }), [period.from, period.to]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pdfDetailLevel, setPdfDetailLevel] = useState(PDF_DETAIL_LEVELS.SUMMARY);
  const [pdfOrientation, setPdfOrientation] = useState(PDF_ORIENTATIONS.PORTRAIT);

  const emptyText = useMemo(() => {
    if (loading) return t("sanitary:reports.messages.loading");
    return t("sanitary:reports.messages.empty");
  }, [loading, t]);

  const loadReports = async (nextFilters = filters, nextPeriod = selectedPeriod) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchSanitaryReports({ ...nextPeriod, ...nextFilters, limit: 100 });
      setReports(asArray(data));
    } catch (err) {
      setError(err?.message || t("sanitary:reports.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports(filters, selectedPeriod);
  }, []);

  const updateFilter = (field, value) => {
    setError("");
    setSuccess("");
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    await loadReports(filters, selectedPeriod);
  };

  const handleGenerate = async () => {
    if (!canWrite) return;
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      await generateSanitaryReport(selectedPeriod);
      setSuccess(t("sanitary:reports.messages.generated"));
      await loadReports(filters, selectedPeriod);
    } catch (err) {
      setError(err?.message || t("sanitary:reports.messages.generateFailed"));
    } finally {
      setWorking(false);
    }
  };

  const handleDetailLevelChange = (value) => {
    setPdfDetailLevel(value);
    setPdfOrientation(value === PDF_DETAIL_LEVELS.SUMMARY
      ? PDF_ORIENTATIONS.PORTRAIT
      : PDF_ORIENTATIONS.LANDSCAPE);
  };

  const handlePdfDownload = async (row) => {
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      await downloadSanitaryReportPdf(row.id, {
        detail_level: pdfDetailLevel,
        orientation: pdfOrientation,
      });
      setSuccess(t("sanitary:reports.messages.downloaded"));
    } catch (err) {
      setError(err?.message || t("sanitary:reports.messages.actionFailed"));
    } finally {
      setWorking(false);
    }
  };

  const handleAction = async (action, row, successKey) => {
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      await action(row.id);
      setSuccess(t(successKey));
      await loadReports(filters, selectedPeriod);
    } catch (err) {
      setError(err?.message || t("sanitary:reports.messages.actionFailed"));
    } finally {
      setWorking(false);
    }
  };

  return (
    <main className="stock-md-page">
      <header className="stock-md-header">
        <div>
          <p className="stock-md-eyebrow">{t("sanitary:reports.eyebrow")}</p>
          <h1 className="stock-md-title">{t("sanitary:reports.title")}</h1>
          <p className="stock-md-subtitle">{t("sanitary:reports.subtitle")}</p>
        </div>
        <button type="button" className="stock-md-button" onClick={() => loadReports(filters, selectedPeriod)} disabled={loading || working}>
          {t("common:actions.refresh", { defaultValue: "Refresh" })}
        </button>
      </header>

      <section className="stock-md-info-box">{t("sanitary:reports.guidance")}</section>
      {error && <div className="stock-md-error">{error}</div>}
      {success && <div className="stock-md-success">{success}</div>}

      <section className="stock-md-card">
        <form className="stock-md-toolbar sanitary-period-filter-row sanitary-reports-toolbar" onSubmit={handleSearch}>
          <EvaluationPeriodPicker
            period={period}
            disabled={loading || working}
            modeLabel={t("sanitary:common.period.mode", { defaultValue: "Typ okresu" })}
            fromLabel={t("sanitary:common.period.from", { defaultValue: "Od" })}
            toLabel={t("sanitary:common.period.to", { defaultValue: "Do" })}
            displayLabel={t("sanitary:common.period.selected", { defaultValue: "Wybrany okres" })}
          />
          <label className="stock-md-field">
            <span>{t("sanitary:reports.filters.status")}</span>
            <select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
              <option value="">{t("sanitary:common.all")}</option>
              {["generated", "regenerated", "locked"].map((status) => (
                <option key={status} value={status}>{t(`sanitary:reports.statuses.${status}`, { defaultValue: status })}</option>
              ))}
            </select>
          </label>
          <label className="stock-md-field sanitary-report-output-field">
            <span>{t("sanitary:reports.pdf.detailLevel")}</span>
            <select value={pdfDetailLevel} onChange={(event) => handleDetailLevelChange(event.target.value)}>
              <option value={PDF_DETAIL_LEVELS.SUMMARY}>{t("sanitary:reports.pdf.levels.summary")}</option>
              <option value={PDF_DETAIL_LEVELS.DETAILED}>{t("sanitary:reports.pdf.levels.detailed")}</option>
              <option value={PDF_DETAIL_LEVELS.AUDIT}>{t("sanitary:reports.pdf.levels.audit")}</option>
            </select>
          </label>
          <label className="stock-md-field sanitary-report-output-field">
            <span>{t("sanitary:reports.pdf.orientation")}</span>
            <select value={pdfOrientation} onChange={(event) => setPdfOrientation(event.target.value)}>
              <option value={PDF_ORIENTATIONS.PORTRAIT}>{t("sanitary:reports.pdf.orientations.portrait")}</option>
              <option value={PDF_ORIENTATIONS.LANDSCAPE}>{t("sanitary:reports.pdf.orientations.landscape")}</option>
            </select>
          </label>
          <button type="submit" className="stock-md-button" disabled={loading || working}>{t("common:actions.search", { defaultValue: "Search" })}</button>
          <button type="button" className="stock-md-button stock-md-button--primary" onClick={handleGenerate} disabled={!canWrite || loading || working}>
            {working ? t("common:actions.saving", { defaultValue: "Saving..." }) : t("sanitary:reports.actions.generate")}
          </button>
        </form>
        <div className="sanitary-report-output-hint">
          {t(`sanitary:reports.pdf.hints.${pdfDetailLevel}`)}
        </div>
      </section>

      <section className="stock-md-card">
        <div className="stock-md-table-wrapper">
          <table className="stock-md-table">
            <thead>
              <tr>
                <th>{t("sanitary:reports.table.generatedAt")}</th>
                <th>{t("sanitary:reports.table.period")}</th>
                <th>{t("sanitary:reports.table.status")}</th>
                <th>{t("sanitary:reports.table.entries")}</th>
                <th>{t("sanitary:reports.table.events")}</th>
                <th>{t("sanitary:reports.table.findings")}</th>
                <th>{t("sanitary:reports.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr><td className="stock-md-empty" colSpan="7">{emptyText}</td></tr>
              ) : reports.map((row) => (
                <tr key={row.id}>
                  <td>{formatDateTime(row.generated_at)}</td>
                  <td>{row.period_from} — {row.period_to}</td>
                  <td><span className="stock-md-pill">{t(`sanitary:reports.statuses.${row.status}`, { defaultValue: row.status })}</span></td>
                  <td>{summaryValue(row, "entries_count")}</td>
                  <td>{summaryValue(row, "events_count")}</td>
                  <td>
                    {summaryValue(row, "findings_count")}
                    {countBySeverity(row.findings, "critical") > 0 && ` · ${countBySeverity(row.findings, "critical")} ${t("sanitary:reports.severity.critical")}`}
                  </td>
                  <td>
                    <div className="stock-md-inline-actions">
                      <button type="button" className="stock-md-link-button" onClick={() => handlePdfDownload(row)} disabled={working}>
                        {t("sanitary:reports.actions.pdf", { defaultValue: "PDF" })}
                      </button>
                      <button type="button" className="stock-md-link-button" onClick={() => handleAction(downloadSanitaryReportExcel, row, "sanitary:reports.messages.downloaded")} disabled={working}>Excel</button>
                      {canWrite && row.status !== "locked" && (
                        <button type="button" className="stock-md-link-button" onClick={() => handleAction(lockSanitaryReport, row, "sanitary:reports.messages.locked")} disabled={working}>{t("sanitary:reports.actions.lock")}</button>
                      )}
                      {canWrite && row.status !== "locked" && (
                        <button type="button" className="stock-md-link-button" onClick={() => handleAction(regenerateSanitaryReport, row, "sanitary:reports.messages.regenerated")} disabled={working}>{t("sanitary:reports.actions.regenerate")}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}


